import { useQuery } from "@tanstack/react-query";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormAutocomplete } from "../../../../common/form-fields-wrappers";
import { LOCALE_DATE_FORMAT } from "../../../../config";
import { ICustomerDataForm } from "../../../../models/models";
import { getIssuePlaces } from "../../../services/http.services";
import countryMappings from "./country-mappings";
import { useCallback, useEffect } from "react";

const DrivingLicenseIssuingPlaceField = ({ referenceProp, disabled, required }: { referenceProp: string, disabled: boolean, required: boolean }) => {
	const { t, i18n } = useTranslation('network');

	const form = useFormContext();

	const [
		drivingLicenseIssuingCountry,
		drivingLicenseIssuingProvince,
		drivingLicenseIssuingPlace,
		drivingLicenseIssueDate
	] = useWatch({
		name: [
			`${referenceProp}.drivingLicenseIssuingCountry`,
			`${referenceProp}.drivingLicenseIssuingProvince`,
			`${referenceProp}.issuingPlace`,
			`${referenceProp}.issuingDate`
		]
	})

	const issuePlacesQuery = useQuery({
		queryKey: ['ntw_issue_places', {
			provinceCode: drivingLicenseIssuingProvince?.codeProvince,
			issueDate: drivingLicenseIssueDate ?? drivingLicenseIssueDate?.format(LOCALE_DATE_FORMAT)
		}],
		queryFn: () => {
			if (
				drivingLicenseIssueDate &&
				!drivingLicenseIssueDate.isValid()
			) {
				return null;
			}
			return getIssuePlaces({
				provinceCode: (drivingLicenseIssuingCountry === 'IT')
					? drivingLicenseIssuingProvince?.codeProvince
					: null,
				issueDate: drivingLicenseIssueDate?.format(LOCALE_DATE_FORMAT)
			})
		}
	});

	const options = [
		...(issuePlacesQuery?.data ?? []),
		...(drivingLicenseIssuingCountry && !["IT", "FR"].includes(drivingLicenseIssuingCountry) ? [{
			description: drivingLicenseIssuingCountry,
			label: drivingLicenseIssuingCountry,
			code: drivingLicenseIssuingCountry
		}] : []),
	]

	useEffect(() => {
		if (drivingLicenseIssuingCountry && drivingLicenseIssuingCountry === 'IT') {
			return;
		} else if (drivingLicenseIssuingCountry && drivingLicenseIssuingCountry === 'FR') {
			const newValue = issuePlacesQuery.data?.find(p => countryMappings[p.description] === drivingLicenseIssuingCountry);
			form.setValue(`${referenceProp}.issuingPlace`, newValue);
		} else if (drivingLicenseIssuingCountry) {
			form.setValue(`${referenceProp}.issuingPlace`, {label: drivingLicenseIssuingCountry, description: drivingLicenseIssuingCountry, code: drivingLicenseIssuingCountry});
		}
	}, [drivingLicenseIssuingCountry, issuePlacesQuery.data]);

	useEffect(() => {
		if (drivingLicenseIssuingCountry === 'IT' && !drivingLicenseIssuingProvince) {
			form.setValue(`${referenceProp}.issuingPlace`, null);
		}
	}, [drivingLicenseIssuingCountry, drivingLicenseIssuingProvince]);

	const getCountryLabel = useCallback((opt: any) => {
		if (drivingLicenseIssuingCountry === 'IT') {
			return opt.description;
		}

		if (drivingLicenseIssuingCountry === 'FR') {
			return t(`countries.${opt.code}`);
		}

		return opt.description;
	}, [drivingLicenseIssuingCountry, i18n.language]);

	return (
		<FormAutocomplete<ICustomerDataForm>
			name={`${referenceProp}.issuingPlace`}
			label={t("customerData.form.label.issuingPlace")}
			options={options}
			fullWidth
			required={required}
			loading={issuePlacesQuery.isInitialLoading}
			disabled={disabled}
			getOptionLabel={getCountryLabel}
		/>
	)
}

export default DrivingLicenseIssuingPlaceField
